/* Firebase Cloud Messaging (web) — substitui o WebPush (PushManager/VAPID).
 * Config via /env.json (VUE_APP_FIREBASE_CONFIG json + VUE_APP_FIREBASE_VAPID_KEY),
 * com fallback para process.env em build.
 */
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, deleteToken, onMessage, isSupported } from 'firebase/messaging';

let app = null;
let messaging = null;

const readConfig = () => {
  try {
    const runtime = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
    const raw = runtime.VUE_APP_FIREBASE_CONFIG || process.env.VUE_APP_FIREBASE_CONFIG;
    if (!raw) return null;
    const cfg = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!cfg || !cfg.apiKey || !cfg.projectId || !cfg.appId) return null;
    return cfg;
  } catch (e) {
    console.warn('[firebase] invalid VUE_APP_FIREBASE_CONFIG:', e);
    return null;
  }
};

const readVapidKey = () => {
  try {
    const runtime = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
    return runtime.VUE_APP_FIREBASE_VAPID_KEY || process.env.VUE_APP_FIREBASE_VAPID_KEY || null;
  } catch (e) {
    return null;
  }
};

export const firebaseSupported = async () => {
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) return false;
    return await isSupported().catch(() => false);
  } catch (e) {
    return false;
  }
};

const ensureMessaging = async () => {
  if (messaging) return messaging;
  const cfg = readConfig();
  if (!cfg) throw new Error('Firebase web config ausente (VUE_APP_FIREBASE_CONFIG)');
  app = getApps().length ? getApp() : initializeApp(cfg);
  messaging = getMessaging(app);
  return messaging;
};

const swRegistration = async () => {
  if (!('serviceWorker' in navigator)) throw new Error('ServiceWorker indisponível');
  try {
    const reg = await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    const regs = await navigator.serviceWorker.getRegistrations().catch(() => []);
    if (regs && regs.length) return regs[0];
    throw e;
  }
};

export const getFCMToken = async () => {
  const m = await ensureMessaging();
  const vapidKey = readVapidKey();
  if (!vapidKey) throw new Error('VAPID key do Firebase ausente (VUE_APP_FIREBASE_VAPID_KEY)');
  const reg = await swRegistration();
  const token = await getToken(m, { vapidKey, serviceWorkerRegistration: reg });
  if (!token) throw new Error('FCM não retornou token');
  return token;
};

export const deleteFCMToken = async () => {
  try {
    const m = await ensureMessaging();
    await deleteToken(m).catch(() => {});
  } catch (e) { /* sem SW/FCM: segue degradado */ }
};

export const onForegroundMessage = async (cb) => {
  try {
    const m = await ensureMessaging();
    return onMessage(m, (payload) => {
      try { cb && cb(payload); } catch (e) { /* sem SW/FCM: segue degradado */ }
    });
  } catch (e) {
    console.warn('[firebase] foreground listener indisponível:', e);
    return () => {};
  }
};

export default { getFCMToken, deleteFCMToken, onForegroundMessage, firebaseSupported };
